import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { askAI } from './ai';
import { Card, CardHeader, Badge, EmptyState, RecordMeta, AHJ_STAGE_LABELS } from './Shared';

const STAGE_VARIANTS = { inbound: 'red', reqs: 'amber', pilot: 'blue', mandate: 'green' };
const STAGE_COLORS = { inbound: '#EF4444', reqs: '#F59E0B', pilot: '#3B82F6', mandate: '#22C55E' };

export default function Dashboard({ profile, onNavigate }) {
  const [data, setData] = useState({ dispatches: [], enrollments: [], ahjs: [] });
  const [aiOut, setAiOut] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showAi, setShowAi] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    const [{ data: d }, { data: e }, { data: a }] = await Promise.all([
      supabase.from('dispatches').select('*').order('created_at', { ascending: false }),
      supabase.from('enrollments').select('*').order('created_at', { ascending: false }),
      supabase.from('ahj_pipeline').select('*').order('created_at', { ascending: false })
    ]);
    setData({ dispatches: d || [], enrollments: e || [], ahjs: a || [] });
  }

  const active = data.dispatches.filter(d => d.status === 'Active' || d.status === 'Dispatched').length;
  const declined = data.dispatches.filter(d => d.status && d.status.startsWith('Declined')).length;
  const placement = data.enrollments.filter(e => e.placement_interest === 'yes').length;
  const stageCounts = { inbound: 0, reqs: 0, pilot: 0, mandate: 0 };
  data.ahjs.forEach(a => { if (stageCounts[a.pipeline_stage] !== undefined) stageCounts[a.pipeline_stage]++; });

  async function runAI() {
    setShowAi(true);
    setAiLoading(true);
    setAiOut('');
    const signals = {};
    const ensure = s => { if (!signals[s]) signals[s] = { dispatch: 0, declined: 0, enrollment: 0, placement: 0, ahj: 0, mandate: 0 }; };
    data.dispatches.forEach(d => { if (d.state) { ensure(d.state); if (d.status && d.status.startsWith('Declined')) signals[d.state].declined++; else signals[d.state].dispatch++; }});
    data.enrollments.forEach(e => { if (e.state) { ensure(e.state); signals[e.state].enrollment++; if (e.placement_interest === 'yes') signals[e.state].placement++; }});
    data.ahjs.forEach(a => { if (a.state) { ensure(a.state); signals[a.state].ahj++; if (a.pipeline_stage === 'mandate') signals[a.state].mandate++; }});
    const statesSummary = Object.entries(signals).map(([st, s]) => `${st}: ${s.dispatch} dispatches, ${s.declined} declined, ${s.enrollment} enrollments, ${s.placement} placement-interested, ${s.ahj} AHJ contacts, ${s.mandate} mandates`).join('; ') || 'no state data yet';
    const prompt = `You are the intelligence layer for AO Group Holdings, a vertically integrated fire watch portfolio (Alpha Omega operations, Fire Watch Academy, JUDGR SaaS platform).
Portfolio signals: ${data.dispatches.length} total dispatches, ${declined} declined jobs, ${data.enrollments.length} academy enrollments, ${data.ahjs.length} AHJ pipeline entries (${stageCounts.mandate} mandates issued). By state: ${statesSummary}.
In 4 concise sentences: (1) the single most significant market opportunity, (2) the single highest execution risk, (3) which state shows the strongest convergence of signals, (4) one immediate action recommendation. Be direct and specific.`;
    try {
      setAiOut(await askAI(prompt, 1000));
    } catch (err) { setAiOut('AI error: ' + err.message); }
    setAiLoading(false);
  }

  const statusVariant = s => {
    if (!s) return 'gray';
    if (s.startsWith('Declined')) return 'red';
    if (s === 'Completed') return 'green';
    if (s === 'Active') return 'blue';
    return 'gray';
  };

  return (
    <div>
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">Active jobs</div>
          <div className="metric-value blue">{active}</div>
          <div className="metric-sub">dispatched / active</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Declined (all time)</div>
          <div className="metric-value red">{declined}</div>
          <div className="metric-sub">geo + capacity</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Placement pipeline</div>
          <div className="metric-value green">{placement}</div>
          <div className="metric-sub">academy → ops</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">AHJ pipeline</div>
          <div className="metric-value amber">{data.ahjs.length}</div>
          <div className="metric-sub">{stageCounts.mandate} mandates issued</div>
        </div>
      </div>

      <div className="two-col-grid">
        <Card>
          <CardHeader title="Recent dispatches" hint={data.dispatches.length + ' total'} />
          {!data.dispatches.length
            ? <EmptyState text="No dispatches yet." action={() => onNavigate('dispatch')} actionLabel="Add one →" />
            : data.dispatches.slice(0, 7).map(d => (
              <div key={d.id} className="list-row">
                <div className="list-row-info">
                  <div className="list-row-name">{d.company || '—'}</div>
                  <div className="list-row-meta">{d.entity} · {d.state || '—'} · {d.industry || '—'}</div>
                  <RecordMeta submitter={d.submitted_by_name} date={d.created_at} />
                </div>
                <Badge variant={statusVariant(d.status)}>{d.status}</Badge>
              </div>
            ))
          }
        </Card>

        <Card>
          <CardHeader title="AHJ pipeline" hint="by stage" />
          {[
            { stage: 'inbound', desc: 'Jurisdictions that reached out' },
            { stage: 'reqs', desc: 'Credential rules captured' },
            { stage: 'pilot', desc: 'Active testing underway' },
            { stage: 'mandate', desc: 'JUDGR required in jurisdiction' },
          ].map(s => (
            <div key={s.stage} className="pipeline-item">
              <div className="pipeline-stage-dot" style={{ background: STAGE_COLORS[s.stage] }} />
              <div className="pipeline-item-info">
                <div className="pipeline-item-name">{AHJ_STAGE_LABELS[s.stage]}</div>
                <div className="pipeline-item-desc">{s.desc}</div>
              </div>
              <div className="pipeline-count" style={{ color: STAGE_COLORS[s.stage] }}>{stageCounts[s.stage]}</div>
            </div>
          ))}
        </Card>
      </div>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="card-title">AI portfolio intelligence</div>
            <div className="card-hint" style={{ marginTop: '2px' }}>Analyzes aggregated signals only — no PII transmitted</div>
          </div>
          <button onClick={runAI} disabled={aiLoading} className="btn btn-primary">
            {aiLoading ? 'Analyzing…' : 'Score portfolio →'}
          </button>
        </div>
        {showAi && (
          <div className="ai-panel">
            <div className="ai-panel-label"><div className="ai-panel-dot" />AI ANALYSIS</div>
            <div className="ai-panel-output">
              {aiLoading ? <span style={{ color: '#94A3B8' }}>Analyzing portfolio signals…</span> : aiOut}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
